import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import { consentScopeMeta, isConsentGranted, type ConsentScope, type ConsentState } from '../domain/consent'
import { defaultNotificationSettings, type NotificationCategory, type NotificationSettingsView } from '../domain/notifications'
import type { DevicePrivacySettings } from '../domain/privacy'
import { disablePmmPush, enablePmmPush, pushCapability, subscribeForegroundPmmPush } from '../notifications/pushClient'
import '../device-center.css'

interface DeviceCenterProps {
  repository: PmmRepository
  onBack(): void
}

interface DeferredInstallPrompt extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const categoryCopy: Record<NotificationCategory, { title: string; summary: string }> = {
  daily_ready: { title: 'Daily practice ready', summary: 'One neutral cue when Today is prepared.' },
  snooze: { title: 'Chosen snoozes', summary: 'Only reminders you deliberately schedule.' },
  weekly_review: { title: 'Weekly council', summary: 'A quiet invitation to reflect on the week.' },
  coach_update: { title: 'Coach update', summary: 'A neutral cue after reviewed material is published.' },
  occasion: { title: 'Important occasions', summary: 'Optional planning cues without names or relationship detail.' },
}

function currentTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Brisbane'
}

function safeMessage(error: unknown): string {
  const code = error instanceof Error ? error.message : ''
  if (code.includes('ios_home_screen')) return 'On iPhone or iPad, add PMM to your Home Screen first, reopen it there, then enable reminders.'
  if (code.includes('permission_denied')) return 'Notifications are blocked in this browser. PMM will not ask again; you can change the permission in browser settings if you choose.'
  if (code.includes('not_supported')) return 'This browser does not support private web reminders. The rest of PMM remains available.'
  if (code.includes('timed_out')) return 'The secure notification registration timed out. No reminders were enabled.'
  return 'That device change could not be completed. No privacy choice was broadened.'
}

export function DeviceCenter({ repository, onBack }: DeviceCenterProps) {
  const [consent, setConsent] = useState<ConsentState>({ version: 0, scopes: {} })
  const [device, setDevice] = useState<DevicePrivacySettings>({ trustedDevice: false, privacyMode: true, persistentCacheActive: false })
  const [settings, setSettings] = useState<NotificationSettingsView>(() => defaultNotificationSettings(currentTimeZone()))
  const [capability, setCapability] = useState<Awaited<ReturnType<typeof pushCapability>>>()
  const [installPrompt, setInstallPrompt] = useState<DeferredInstallPrompt>()
  const [standalone, setStandalone] = useState(false)
  const [updateReady, setUpdateReady] = useState(false)
  const [busy, setBusy] = useState('loading')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const pushAllowed = isConsentGranted(consent, 'push_notifications')
  const offlineAllowed = isConsentGranted(consent, 'offline_persistent_cache')
  const browserDenied = capability?.permission === 'denied'
  const installLabel = capability?.requiresIosInstall ? 'Add from Safari Share menu' : 'Install PMM on this device'

  useEffect(() => {
    let active = true
    setBusy('loading')
    Promise.all([repository.getPrivacySnapshot(), repository.getNotificationSettings(), pushCapability()]).then(([privacy, notifications, nextCapability]) => {
      if (!active) return
      setConsent(privacy.consent)
      setDevice(privacy.device)
      setSettings(notifications)
      setCapability(nextCapability)
      setStandalone(window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true)
      setBusy('')
    }).catch(() => {
      if (active) { setError('Device settings could not be loaded. No choice was changed.'); setBusy('') }
    })
    const beforeInstall = (event: Event) => { event.preventDefault(); setInstallPrompt(event as DeferredInstallPrompt) }
    const installed = () => { setStandalone(true); setInstallPrompt(undefined); setNotice('PMM is installed on this device.') }
    const update = () => setUpdateReady(true)
    window.addEventListener('beforeinstallprompt', beforeInstall)
    window.addEventListener('appinstalled', installed)
    window.addEventListener('pmm:update-ready', update)
    let stopForeground: () => void = () => undefined
    void subscribeForegroundPmmPush(() => setNotice('A test reminder arrived while PMM was open.')).then((stop) => { stopForeground = stop })
    return () => {
      active = false
      stopForeground()
      window.removeEventListener('beforeinstallprompt', beforeInstall)
      window.removeEventListener('appinstalled', installed)
      window.removeEventListener('pmm:update-ready', update)
    }
  }, [repository])

  async function decide(scope: ConsentScope, grant: boolean, source: 'privacy_centre' | 'point_of_collection' = 'point_of_collection', base = consent) {
    const current = base.scopes[scope]
    const next = await repository.recordConsentDecision({
      scope,
      decision: grant ? 'grant' : current?.granted ? 'revoke' : 'deny',
      previousEventId: current?.lastEventId ?? null,
      uiSource: source,
    })
    setConsent(next)
    return next
  }

  async function install() {
    if (!installPrompt) return
    setError('')
    await installPrompt.prompt()
    const choice = await installPrompt.userChoice
    setInstallPrompt(undefined)
    setNotice(choice.outcome === 'accepted' ? 'Installation accepted.' : 'Installation dismissed. PMM will not interrupt you again.')
  }

  async function enableReminders() {
    if (browserDenied) return
    setBusy('push')
    setError('')
    let latest = consent
    let newlyGranted = false
    try {
      if (!pushAllowed) { latest = await decide('push_notifications', true); newlyGranted = true }
      await enablePmmPush(repository)
      const next = await repository.updateNotificationSettings({
        enabled: true,
        categories: settings.categories,
        quietStart: settings.quietStart,
        quietEnd: settings.quietEnd,
        dailyTime: settings.dailyTime,
        weeklyDay: settings.weeklyDay,
        weeklyTime: settings.weeklyTime,
        timeZone: currentTimeZone(),
      })
      setSettings(next)
      setCapability(await pushCapability())
      setNotice('Private reminders are enabled. Every visible notification uses neutral lock-screen copy.')
    } catch (reason) {
      if (newlyGranted) {
        await decide('push_notifications', false, 'point_of_collection', latest).catch(() => undefined)
      }
      setCapability(await pushCapability().catch(() => capability))
      setError(safeMessage(reason))
    } finally { setBusy('') }
  }

  async function disableReminders() {
    setBusy('push')
    setError('')
    try {
      await disablePmmPush(repository)
      if (pushAllowed) await decide('push_notifications', false)
      setSettings((current) => ({ ...current, enabled: false, activeInstallationCount: 0 }))
      setNotice('Private reminders are off and this installation was revoked. PMM will not prompt again.')
    } catch (reason) { setError(safeMessage(reason)) }
    finally { setBusy('') }
  }

  async function saveSettings() {
    setBusy('settings')
    setError('')
    try {
      const next = await repository.updateNotificationSettings({
        enabled: settings.enabled && pushAllowed && capability?.permission === 'granted',
        categories: settings.categories,
        quietStart: settings.quietStart,
        quietEnd: settings.quietEnd,
        dailyTime: settings.dailyTime,
        weeklyDay: settings.weeklyDay,
        weeklyTime: settings.weeklyTime,
        timeZone: currentTimeZone(),
      })
      setSettings(next)
      setNotice('Reminder timing saved for your current timezone.')
    } catch (reason) { setError(safeMessage(reason)) }
    finally { setBusy('') }
  }

  async function sendTest() {
    setBusy('test')
    setError('')
    try {
      const result = await repository.sendTestNotification()
      setNotice(result.state === 'sent' ? 'A neutral test reminder was sent.' : result.state === 'duplicate' ? 'The test was already sent; duplicate delivery was suppressed.' : 'No active installation was available for the test.')
    } catch (reason) { setError(safeMessage(reason)) }
    finally { setBusy('') }
  }

  async function trustDevice(trustedDevice: boolean) {
    setBusy('offline')
    setError('')
    try {
      const next = await repository.updateDevicePrivacy({ trustedDevice, privacyMode: device.privacyMode })
      setDevice(next)
      setNotice(trustedDevice ? 'This browser is marked private and trusted. Offline storage still requires separate consent.' : 'Trusted-device access is off and encrypted PMM device data was cleared.')
    } catch { setError('The trusted-device preference was not changed.') }
    finally { setBusy('') }
  }

  async function toggleOffline(grant: boolean) {
    if (grant && !device.trustedDevice) { setError('Mark this as a private, trusted device before enabling encrypted offline access.'); return }
    setBusy('offline')
    setError('')
    try {
      await decide('offline_persistent_cache', grant)
      const next = await repository.getPrivacySnapshot()
      setDevice(next.device)
      setNotice(grant ? 'Encrypted offline Today is enabled on this trusted browser.' : 'Offline access is off and the encrypted device vault was cleared.')
    } catch { setError('The offline-storage choice was not changed.') }
    finally { setBusy('') }
  }

  const categories = useMemo(() => Object.keys(categoryCopy) as NotificationCategory[], [])

  return (
    <main className="device-centre" aria-labelledby="device-title">
      <section className="device-hero">
        <button type="button" className="back-action" onClick={onBack}>Back to practice</button>
        <p className="eyebrow">Install & reminders</p>
        <h1 id="device-title">Present when useful. Quiet by design.</h1>
        <p>Install PMM, choose neutral reminders and decide whether this trusted browser may keep one encrypted Today plan for offline use.</p>
      </section>

      {(notice || error) && <div className={error ? 'device-message is-error' : 'device-message'} role={error ? 'alert' : 'status'}>{error || notice}</div>}
      {updateReady && <section className="update-card"><div><strong>A private update is ready</strong><p>Reload to use the newest verified PMM shell.</p></div><button type="button" onClick={() => window.location.reload()}>Reload now</button></section>}

      <section className="device-grid" aria-busy={busy === 'loading'}>
        <article className="device-card install-card">
          <div className="device-card-heading"><span>01</span><div><p className="eyebrow">App installation</p><h2>Keep PMM within reach</h2></div></div>
          {standalone ? <div className="device-state is-on"><span />Installed as an app</div> : capability?.requiresIosInstall ? <div className="install-guidance"><strong>Install on iPhone or iPad</strong><ol><li>Open this page in Safari.</li><li>Tap Share.</li><li>Choose Add to Home Screen.</li><li>Open PMM from its new icon before enabling reminders.</li></ol></div> : installPrompt ? <button type="button" className="primary-device-action" onClick={() => void install()}>{installLabel}</button> : <p className="device-explainer">Use your browser menu and choose <strong>Install app</strong> or <strong>Add to Home Screen</strong>. Installation is optional.</p>}
          <small>PMM never shows an installation prompt repeatedly after you dismiss it.</small>
        </article>

        <article className="device-card reminder-card">
          <div className="device-card-heading"><span>02</span><div><p className="eyebrow">Private reminders</p><h2>Neutral on every lock screen</h2></div></div>
          <div className={`device-state ${settings.enabled && pushAllowed ? 'is-on' : ''}`}><span />{settings.enabled && pushAllowed ? `${settings.activeInstallationCount} active installation${settings.activeInstallationCount === 1 ? '' : 's'}` : browserDenied ? 'Blocked by browser — no more prompts' : 'Reminders off'}</div>
          <p className="notification-preview"><span>Preview</span><strong>Primal Masculine Man</strong><small>A quiet moment is ready.</small></p>
          <p>{consentScopeMeta.push_notifications.collectionNotice}</p>
          {settings.enabled && pushAllowed ? <button type="button" className="secondary-device-action" disabled={busy === 'push'} onClick={() => void disableReminders()}>Turn reminders off</button> : <button type="button" className="primary-device-action" disabled={busy === 'push' || browserDenied || capability?.supported === false || capability?.requiresIosInstall} onClick={() => void enableReminders()}>Enable private reminders</button>}
        </article>

        <article className="device-card schedule-card">
          <div className="device-card-heading"><span>03</span><div><p className="eyebrow">Timing and categories</p><h2>You set the rhythm</h2></div></div>
          <div className="category-switches">
            {categories.map((category) => <label key={category}><span><strong>{categoryCopy[category].title}</strong><small>{categoryCopy[category].summary}</small></span><input type="checkbox" checked={settings.categories[category]} onChange={(event) => setSettings((current) => ({ ...current, categories: { ...current.categories, [category]: event.target.checked } }))} /></label>)}
          </div>
          <div className="schedule-fields">
            <label><span>Daily cue</span><input type="time" value={settings.dailyTime} onChange={(event) => setSettings((current) => ({ ...current, dailyTime: event.target.value }))} /></label>
            <label><span>Weekly day</span><select value={settings.weeklyDay} onChange={(event) => setSettings((current) => ({ ...current, weeklyDay: Number(event.target.value) }))}><option value="0">Sunday</option><option value="1">Monday</option><option value="2">Tuesday</option><option value="3">Wednesday</option><option value="4">Thursday</option><option value="5">Friday</option><option value="6">Saturday</option></select></label>
            <label><span>Weekly cue</span><input type="time" value={settings.weeklyTime} onChange={(event) => setSettings((current) => ({ ...current, weeklyTime: event.target.value }))} /></label>
            <label><span>Quiet from</span><input type="time" value={settings.quietStart} onChange={(event) => setSettings((current) => ({ ...current, quietStart: event.target.value }))} /></label>
            <label><span>Quiet until</span><input type="time" value={settings.quietEnd} onChange={(event) => setSettings((current) => ({ ...current, quietEnd: event.target.value }))} /></label>
          </div>
          <p className="timezone-note">Current timezone: <strong>{currentTimeZone()}</strong>. Daylight-saving changes are resolved server-side from this IANA timezone.</p>
          <div className="device-actions"><button type="button" disabled={busy === 'settings'} onClick={() => void saveSettings()}>Save timing</button><button type="button" className="secondary-device-action" disabled={!settings.enabled || busy === 'test'} onClick={() => void sendTest()}>Send neutral test</button></div>
        </article>

        <article className="device-card offline-card">
          <div className="device-card-heading"><span>04</span><div><p className="eyebrow">Encrypted offline</p><h2>One Today plan, not your history</h2></div></div>
          <label className="trust-choice"><span><strong>Private, trusted device</strong><small>Off on shared computers. Turning this off purges the vault.</small></span><input type="checkbox" checked={device.trustedDevice} onChange={(event) => void trustDevice(event.target.checked)} /></label>
          <div className="offline-boundary"><div><span>Available offline</span><strong>Current Today plan and queued action receipts</strong></div><div><span>Never cached here</span><strong>Coach reasons, AI payloads, partner profiles or full history</strong></div></div>
          <p>Content is encrypted with a non-exportable key held by this browser. Clearing site data or signing out removes access. This protects data at rest; it cannot make a compromised browser trustworthy.</p>
          {offlineAllowed && device.persistentCacheActive ? <button type="button" className="secondary-device-action" disabled={busy === 'offline'} onClick={() => void toggleOffline(false)}>Disable and clear offline data</button> : <button type="button" className="primary-device-action" disabled={busy === 'offline' || !device.trustedDevice} onClick={() => void toggleOffline(true)}>Enable encrypted offline Today</button>}
          <div className={`device-state ${offlineAllowed && device.persistentCacheActive ? 'is-on' : ''}`}><span />{offlineAllowed && device.persistentCacheActive ? 'Encrypted offline vault active' : device.trustedDevice ? 'Trusted, awaiting offline consent' : 'Memory-only mode'}</div>
        </article>
      </section>
    </main>
  )
}
